import { useTranslation } from '@hopin-team/provider-translation';
import React from 'react';

import { EventUtilizationData } from '../hooks/use-admin-kpis';

type TEventTypeKey = keyof EventUtilizationData['eventTypeDistribution'];

const BADGE_CONFIG = [
  {
    key: 'webinar' as TEventTypeKey,
    i18nKey: 'analytics.admin.event-type-webinar',
    colorClasses: 'bg-webinar-bg text-webinar-text',
  },
  {
    key: 'virtual' as TEventTypeKey,
    i18nKey: 'analytics.admin.event-type-virtual',
    colorClasses: 'bg-virtual-bg text-virtual-text',
  },
  {
    key: 'hybrid' as TEventTypeKey,
    i18nKey: 'analytics.admin.event-type-hybrid',
    colorClasses: 'bg-hybrid-bg text-hybrid-text',
  },
  {
    key: 'onSite' as TEventTypeKey,
    i18nKey: 'analytics.admin.event-type-on-site',
    colorClasses: 'bg-on-site-bg text-on-site-text',
  },
] as const;

const BADGE_ROWS = [
  ['webinar', 'virtual'],
  ['hybrid', 'onSite'],
] as const satisfies readonly (readonly TEventTypeKey[])[];

type EventTypeBadgesProps = {
  distribution: EventUtilizationData['eventTypeDistribution'];
};

export const EventTypeBadges = ({ distribution }: EventTypeBadgesProps) => {
  const { t } = useTranslation();

  return (
    <div
      className="flex h-[94px] w-[218px] shrink-0 flex-col justify-center gap-2.5 px-4 pt-4"
      data-testid="event-type-badges"
    >
      {BADGE_ROWS.map((row, rowIndex) => (
        <div
          key={row.join('-')}
          className="flex items-center gap-2.5"
          data-testid={`event-type-badge-row-${rowIndex + 1}`}
        >
          {row.map(key => {
            const { i18nKey, colorClasses } = BADGE_CONFIG.find(
              badge => badge.key === key,
            )!;

            return (
              <span
                key={key}
                className={`inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded px-2 text-xs font-medium ${colorClasses}`}
                data-testid={`event-type-badge-${i18nKey}`}
              >
                {t(i18nKey)} {distribution[key]}%
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
};
